import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../../contexts/AuthContext';
import { CREDIT_PACKAGES, createCreditPurchaseSession, confirmCreditPurchase } from '../../services/stripeService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../dashboard/components/Header';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';


// Initialize Stripe
const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

// Payment Form Component
const PaymentForm = ({ clientSecret, selectedPackage, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setErrorMessage('');

    try {
      const { error, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/purchase-success`,
        },
        redirect: 'if_required'
      });

      if (error) {
        setErrorMessage(error?.message);
        onError?.(error);
      } else if (paymentIntent && paymentIntent?.status === 'succeeded') {
        // Payment successful - confirm on backend
        const result = await confirmCreditPurchase(paymentIntent?.id);
        
        if (result?.success) {
          onSuccess?.(result);
        } else {
          setErrorMessage('Payment processed but confirmation failed. Please contact support.');
        }
      }
    } catch (err) {
      setErrorMessage(err?.message || 'Payment failed. Please try again.');
      onError?.(err);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement />
      
      {errorMessage && (
        <div className="p-4 bg-error/10 border border-error/30 rounded-lg">
          <p className="text-error text-sm">{errorMessage}</p>
        </div>
      )}

      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        iconName={processing ? 'Loader2' : 'Lock'}
        disabled={!stripe || processing}
      >
        {processing ? 'Processing Payment...' : `Pay ${selectedPackage?.priceDisplay}`}
      </Button>

      <div className="flex items-center justify-center gap-6 pt-4 border-t border-border">
        <div className="flex items-center gap-2">
          <Icon name="Shield" size={16} color="var(--color-success)" />
          <span className="text-xs text-muted-foreground">Secure Payment</span>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="Lock" size={16} color="var(--color-success)" />
          <span className="text-xs text-muted-foreground">256-bit SSL</span>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="CreditCard" size={16} color="var(--color-success)" />
          <span className="text-xs text-muted-foreground">Powered by Stripe</span>
        </div>
      </div>
    </form>
  );
};

const PurchaseCredits = () => {
  const navigate = useNavigate();
  const { profile, refreshProfile } = useAuth();
  const [selectedPackage, setSelectedPackage] = useState('value');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  const handleSelectPackage = async (packageId) => {
    setSelectedPackage(packageId);
    setShowPaymentForm(false);
    setClientSecret(null);
    setError(null);
  };

  const handleProceedToCheckout = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await createCreditPurchaseSession(selectedPackage, profile?.id);

      if (result?.success) {
        setClientSecret(result?.clientSecret);
        setShowPaymentForm(true);
      } else {
        setError(result?.error || 'Failed to create payment session');
      }
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (result) => {
    // Refresh user profile to get updated credit balance
    await refreshProfile?.();
    
    // Show success message and redirect
    navigate('/dashboard', { 
      state: { 
        message: `Successfully purchased ${result?.creditsAdded} credits! Your new balance is ${result?.newBalance} credits.`,
        type: 'success'
      }
    });
  };

  const handlePaymentError = (error) => {
    setError(error?.message || 'Payment failed. Please try again.');
    setShowPaymentForm(false);
    setClientSecret(null);
  };

  const currentCredits = profile?.credits_remaining || 0;
  const creditLimit = profile?.subscription_tier === 'free' ? 50 : 
                     profile?.subscription_tier === 'professional' ? 500 : 2000;
  
  const selectedPkg = CREDIT_PACKAGES?.find(pkg => pkg?.id === selectedPackage);

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigation userRole={profile?.role || 'clinician'} />

      <div className="pt-header">
        <div className="max-w-7xl mx-auto container-padding py-6 sm:py-8 md:py-12">
          {/* Header Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
              <Icon name="Zap" size={20} color="var(--color-primary)" />
              <span className="text-sm font-semibold text-primary">Purchase Additional Credits</span>
            </div>
            
            <h1 className="text-4xl font-bold text-foreground mb-4">
              Boost Your AI Diagnostic Power
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose the perfect credit package for your clinical needs. More credits = better value and bigger savings!
            </p>

            {/* Current Balance Card */}
            <div className="inline-block mt-6 px-6 py-4 bg-gradient-to-r from-primary/20 to-accent/20 rounded-xl border border-primary/30">
              <div className="flex items-center gap-4">
                <div className="text-left">
                  <p className="text-sm text-muted-foreground">Current Balance</p>
                  <p className="text-2xl font-bold text-foreground">{currentCredits} Credits</p>
                </div>
                <div className="h-12 w-px bg-border"></div>
                <div className="text-left">
                  <p className="text-sm text-muted-foreground">Monthly Limit</p>
                  <p className="text-2xl font-bold text-foreground">{creditLimit} Credits</p>
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-8 p-4 bg-error/10 border border-error/30 rounded-lg flex items-center gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" />
              <p className="text-error">{error}</p>
            </div>
          )}

          {!showPaymentForm ? (
            <>
              {/* Credit Packages */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                {CREDIT_PACKAGES?.map((pkg) => (
                  <div
                    key={pkg?.id}
                    className={`clinical-card p-6 cursor-pointer transition-all duration-300 hover:scale-105 ${
                      selectedPackage === pkg?.id
                        ? 'ring-2 ring-primary shadow-xl'
                        : 'hover:shadow-lg'
                    } ${pkg?.popular ? 'relative' : ''}`}
                    onClick={() => handleSelectPackage(pkg?.id)}
                  >
                    {/* Popular Badge */}
                    {pkg?.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-primary to-accent rounded-full">
                        <span className="text-xs font-bold text-white">MOST POPULAR</span>
                      </div>
                    )}

                    {/* Discount Badge */}
                    {pkg?.discount && (
                      <div className="absolute top-4 right-4 px-2 py-1 bg-success text-white text-xs font-bold rounded">
                        {pkg?.discount}
                      </div>
                    )}

                    {/* Package Info */}
                    <div className="text-center mb-4">
                      <h3 className="text-xl font-bold text-foreground mb-2">{pkg?.name}</h3>
                      <div className="mb-3">
                        <span className="text-4xl font-bold text-primary">{pkg?.priceDisplay}</span>
                      </div>
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-full">
                        <Icon name="Coins" size={16} color="var(--color-primary)" />
                        <span className="text-lg font-bold text-primary">{pkg?.credits} Credits</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">{pkg?.perCreditCost} per credit</p>
                      {pkg?.savings && (
                        <p className="text-sm font-semibold text-success mt-1">Save {pkg?.savings}!</p>
                      )}
                    </div>

                    {/* Features */}
                    <div className="space-y-2 mb-6">
                      {pkg?.features?.map((feature, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <Icon 
                            name="CheckCircle2" 
                            size={16} 
                            color="var(--color-success)" 
                            className="mt-0.5 flex-shrink-0"
                          />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Select Button */}
                    <Button
                      variant={selectedPackage === pkg?.id ? 'primary' : 'outline'}
                      fullWidth
                      iconName={selectedPackage === pkg?.id ? 'Check' : 'ShoppingCart'}
                      disabled={loading}
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleSelectPackage(pkg?.id);
                      }}
                    >
                      {selectedPackage === pkg?.id ? 'Selected' : 'Select Package'}
                    </Button>
                  </div>
                ))}
              </div>

              {/* Purchase Section */}
              <div className="clinical-card p-8 max-w-2xl mx-auto">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-foreground mb-2">Ready to Purchase?</h2>
                  <p className="text-muted-foreground">
                    Complete your purchase securely with Stripe. Your credits will be added instantly.
                  </p>
                </div>

                {/* Selected Package Summary */}
                {selectedPkg && (
                  <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Selected Package</p>
                        <p className="text-xl font-bold text-foreground">{selectedPkg?.name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Total Credits</p>
                        <p className="text-2xl font-bold text-primary">{selectedPkg?.credits}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <span className="text-lg font-semibold text-foreground">Total Amount</span>
                      <span className="text-3xl font-bold text-primary">{selectedPkg?.priceDisplay}</span>
                    </div>
                  </div>
                )}

                {/* Proceed Button */}
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  iconName="Lock"
                  disabled={loading || !selectedPackage}
                  onClick={handleProceedToCheckout}
                >
                  {loading ? 'Processing...' : 'Proceed to Secure Checkout'}
                </Button>
              </div>
            </>
          ) : (
            /* Payment Form Section */
            (<div className="clinical-card p-8 max-w-2xl mx-auto">
              <div className="mb-6">
                <button
                  onClick={() => setShowPaymentForm(false)}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name="ArrowLeft" size={20} />
                  <span>Back to Packages</span>
                </button>
              </div>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-foreground mb-2">Complete Your Purchase</h2>
                <p className="text-muted-foreground">
                  Enter your payment details to complete the purchase
                </p>
              </div>
              {/* Selected Package Summary */}
              {selectedPkg && (
                <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-6 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Package</p>
                      <p className="text-lg font-bold text-foreground">{selectedPkg?.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Amount</p>
                      <p className="text-2xl font-bold text-primary">{selectedPkg?.priceDisplay}</p>
                    </div>
                  </div>
                </div>
              )}
              {/* Stripe Payment Form */}
              {clientSecret && (
                <Elements 
                  stripe={stripePromise} 
                  options={{ 
                    clientSecret,
                    appearance: {
                      theme: 'stripe',
                      variables: {
                        colorPrimary: '#2563eb',
                      }
                    }
                  }}
                >
                  <PaymentForm
                    clientSecret={clientSecret}
                    selectedPackage={selectedPkg}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                  />
                </Elements>
              )}
            </div>)
          )}

          {/* Why Purchase Credits Section */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="clinical-card p-6 text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Zap" size={24} color="var(--color-primary)" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Instant Activation</h3>
              <p className="text-sm text-muted-foreground">
                Credits are added to your account immediately after purchase
              </p>
            </div>

            <div className="clinical-card p-6 text-center">
              <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="TrendingDown" size={24} color="var(--color-success)" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Better Value</h3>
              <p className="text-sm text-muted-foreground">
                Save up to 50% compared to pay-as-you-go pricing
              </p>
            </div>

            <div className="clinical-card p-6 text-center">
              <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="RefreshCw" size={24} color="var(--color-accent)" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No Expiration</h3>
              <p className="text-sm text-muted-foreground">
                Enterprise credits never expire - use them at your own pace
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseCredits;